import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Website } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { ItemIcon, DropdownButton, CheckableDropdownItem, Dropdown } from "@similarweb/ui-components";

import type { GscSite } from "../types";

import { ConfirmModal } from "./ConfirmModal";
import { Label, SmallItemIcon, DropdownWrapper, DropdownItem } from "./styles";
import { dropdownTextSearchFilter } from "./utils";

type Props = {
    properties: GscSite[] | undefined;
    selectedSite: GscSite | undefined;
    onSelect: (site: GscSite) => void;
    isDisabled: boolean;
    requireConfirmation?: boolean;
};

export const WebsitePicker = ({
    properties,
    selectedSite,
    onSelect,
    isDisabled,
    requireConfirmation = false,
}: Props) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [pendingSite, setPendingSite] = useState<GscSite | null>(null);

    const handleSelect = useCallback(
        (item: { id: string }) => {
            const site = properties?.find((p) => p.site === item.id);
            if (!site) {
                return;
            }

            if (requireConfirmation && site.site !== selectedSite?.site) {
                setPendingSite(site);
                return;
            }

            onSelect(site);
        },
        [properties, selectedSite, onSelect, requireConfirmation],
    );

    const handleConfirm = useCallback(() => {
        if (pendingSite) {
            onSelect(pendingSite);
        }
        setPendingSite(null);
    }, [pendingSite, onSelect]);

    const handleCancel = useCallback(() => setPendingSite(null), []);

    const dropdownItems = useMemo(
        () =>
            properties
                ? properties.map((site) => (
                      <CheckableDropdownItem
                          key={site.site}
                          id={site.site}
                          text={site.displayText}
                          selected={site.site === selectedSite?.site}
                          data-automation={`website-item-${site.site}`}
                      >
                          <DropdownItem>
                              <ItemIcon iconSrc={site.faviconUrl} />
                              {site.displayText}
                          </DropdownItem>
                      </CheckableDropdownItem>
                  ))
                : [],
        [properties, selectedSite],
    );

    const placeholder = translate("rankTracker.gsc.websitePicker.placeholder");

    return (
        <>
            <DropdownWrapper
                data-automation="gsc-website-picker"
                hasValue={!!selectedSite}
            >
                <Label>{translate("rankTracker.gsc.websitePicker.label")}</Label>
                <Dropdown
                    isOpen={isOpen}
                    disabled={isDisabled}
                    appendTo="body"
                    dropdownPopupPlacement="ontop-left"
                    hasSearch
                    searchPlaceHolder={placeholder}
                    searchFilter={dropdownTextSearchFilter}
                    onToggle={setIsOpen}
                    onClick={handleSelect}
                    closeOnItemClick
                    noResultsMessage={translate("rankTracker.gsc.websitePicker.noResults")}
                >
                    {[
                        <DropdownButton
                            key="trigger"
                            height={40}
                            disabled={!properties || isDisabled}
                        >
                            <DropdownItem>
                                {selectedSite ? (
                                    <SmallItemIcon iconSrc={selectedSite.faviconUrl} />
                                ) : (
                                    <Website
                                        size="sm"
                                        color={colorsPalettes.carbon[200]}
                                    />
                                )}
                                {selectedSite?.displayText ?? placeholder}
                            </DropdownItem>
                        </DropdownButton>,
                        ...dropdownItems,
                    ]}
                </Dropdown>
            </DropdownWrapper>
            <ConfirmModal
                isOpen={!!pendingSite}
                description={translate("rankTracker.gsc.confirmModal.website.description")}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
            />
        </>
    );
};
